import {
  addStoreLocation,
  updateStoreLocation,
  StoreLocation,
  StoreLocationPayload,
} from "@/api/store-location";
import { useMutation } from "@tanstack/react-query";
import { Form, Input, message, Modal, Switch, TimePicker } from "antd";
import { useEffect } from "react";
import dayjs from "dayjs";

type IProps = {
  open: boolean;
  onCancel: () => void;
  refresh?: () => void;
  data?: StoreLocation | null;
};

export default function AddModal({ open, onCancel, refresh, data }: IProps) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      // Parse hours string (e.g., "8:00 - 22:00") into TimePicker format
      let hoursRange = undefined;
      if (data.hours) {
        const parts = data.hours.split(" - ");
        if (parts.length === 2) {
          hoursRange = [
            dayjs(parts[0], "HH:mm"),
            dayjs(parts[1], "HH:mm"),
          ];
        }
      }
      form.setFieldsValue({
        ...data,
        hoursRange,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ active: true });
    }
  }, [data, form]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (values: StoreLocationPayload) => {
      if (data) {
        return updateStoreLocation(data.id, values);
      }
      return addStoreLocation(values);
    },
    onSuccess(response: any) {
      if (response?.error) {
        message.error(response?.error || "Đã có lỗi xảy ra");
        return;
      }
      message.success(data ? "Cập nhật thành công" : "Thêm thành công");
      handleCancel();
      refresh?.();
    },
  });

  const handleOk = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      // Convert hoursRange to string format "HH:mm - HH:mm"
      const hours = values.hoursRange
        ? `${values.hoursRange[0].format("HH:mm")} - ${values.hoursRange[1].format("HH:mm")}`
        : "";
      const payload = {
        ...values,
        hours,
      };
      delete payload.hoursRange;
      mutate(payload);
    } catch (error) {
      return;
    }
  };

  return (
    <Modal
      title={data ? "Cập nhật địa chỉ cửa hàng" : "Thêm địa chỉ cửa hàng"}
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      centered
      confirmLoading={isPending}
      okText={data ? "Cập nhật" : "Thêm"}
      cancelText="Hủy"
      destroyOnHidden
      width={600}
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label="Tên cửa hàng"
          name="name"
          rules={[{ required: true, message: "Vui lòng nhập tên cửa hàng" }]}
        >
          <Input placeholder="Nhập tên cửa hàng" />
        </Form.Item>
        <Form.Item
          label="Địa chỉ"
          name="address"
          rules={[{ required: true, message: "Vui lòng nhập địa chỉ" }]}
        >
          <Input.TextArea rows={2} placeholder="Nhập địa chỉ cửa hàng" />
        </Form.Item>
        <Form.Item
          label="Số điện thoại"
          name="phone"
          rules={[{ required: true, message: "Vui lòng nhập số điện thoại" }]}
        >
          <Input placeholder="Nhập số điện thoại" />
        </Form.Item>
        <Form.Item
          label="Giờ mở cửa"
          name="hoursRange"
          rules={[{ required: true, message: "Vui lòng chọn giờ mở cửa" }]}
        >
          <TimePicker.RangePicker
            format="HH:mm"
            placeholder={["Giờ mở cửa", "Giờ đóng cửa"]}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="Link Google Maps" name="mapUrl">
          <Input placeholder="Nhập link Google Maps (tùy chọn)" />
        </Form.Item>
        <Form.Item label="Trạng thái" name="active" valuePropName="checked">
          <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
